import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import api from '../utils/api';

const PUBLIC_BASE = process.env.PUBLIC_URL || '';
const CART_FALLBACK_IMAGE = `${PUBLIC_BASE}/images/jewellery-img1.jpg`;

const resolveCartImage = (value) => {
  if (!value || typeof value !== 'string') {
    return CART_FALLBACK_IMAGE;
  }

  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) {
    return value;
  }

  if (value.startsWith(PUBLIC_BASE) || value.startsWith('/images/')) {
    return `${value.startsWith('/images/') ? PUBLIC_BASE : ''}${value}`;
  }

  return value;
};

const getCartItemImage = (item) =>
  resolveCartImage(item?.image || item?.images?.front || item?.product?.image || item?.product?.images?.front);

/* ── CART ────────────────────────────────────── */
export default function CartPage() {
  const { cart, removeFromCart, updateQty, cartTotal } = useStore();
  const [freeShippingAbove, setFreeShippingAbove] = useState(null);

  useEffect(() => {
    const loadShippingSettings = async () => {
      try {
        const response = await api.get('/store-settings');
        const threshold = response.data?.data?.shippingAlerts?.freeShippingAbove;
        if (typeof threshold === 'number') {
          setFreeShippingAbove(threshold);
        }
      } catch (error) {
        console.error('Error loading shipping settings:', error);
      }
    };

    loadShippingSettings();
  }, []);

  const productShippingTotal = cart.reduce((total, item) => {
    const shippingFee = Number(item.shippingFee);
    return total + (Number.isFinite(shippingFee) && shippingFee >= 0 ? shippingFee * item.qty : 0);
  }, 0);
  const qualifiesForFreeShipping =
    typeof freeShippingAbove === 'number' && cartTotal > freeShippingAbove;
  const shipping = qualifiesForFreeShipping ? 0 : productShippingTotal;

  if (!cart.length) return (
    <div className="cart-page-wrap cart-empty-state">
      <div className="cart-empty-icon">
        <i className="fas fa-bag-shopping" />
      </div>
      <h2 className="cart-empty-title">Your cart is empty</h2>
      <p className="cart-empty-desc">Add some products to get started</p>
      <Link to="/shop" className="btn-gold">Continue Shopping</Link>
    </div>
  );

  return (
    <div className="cart-page-wrap">
      <div className="cart-page-hero">
        <div className="container">
          <h1 className="cart-page-title">
            Your <em className="cart-page-title-em">Cart</em>
          </h1>
          <p className="cart-page-count">{cart.reduce((s,i) => s + i.qty, 0)} items</p>
        </div>
      </div>

      <div className="container cart-page-body">
        <div className="row g-4">
          <div className="col-lg-8">
            {cart.map(item => (
              <div key={`${item.id}-${item.variant}`} className="cart-item">
                <div className="cart-item-img">
                  <img
                  src={getCartItemImage(item) || CART_FALLBACK_IMAGE}
                    alt={item.name}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = CART_FALLBACK_IMAGE;
                  }}
                />
              </div>
                <div className="cart-item-info">
                  <Link to={`/product/${item.id}`} className="cart-item-name">
                    {item.name}
                  </Link>
                  <p className="cart-item-variant">{item.variant}</p>
                  <div className="cart-item-controls">
                    <div className="qty-ctrl">
                      <button className="qty-btn" onClick={() => item.qty > 1 ? updateQty(item.id, item.variant, item.qty - 1) : removeFromCart(item.id, item.variant)}>−</button>
                      <span className="qty-val">{item.qty}</span>
                      <button className="qty-btn" onClick={() => updateQty(item.id, item.variant, item.qty + 1)}>+</button>
                    </div>
                    <span className="cart-item-price">
                      ₹{(item.price * item.qty).toLocaleString('en-IN')}
                    </span>
                    {item.qty > 1 && (
                      <span className="cart-item-price-each">
                        ₹{item.price.toLocaleString('en-IN')} each
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeFromCart(item.id, item.variant)}
                  className="cart-item-remove-btn"
                  aria-label="Remove item"
                >
                  <i className="fas fa-trash-can" />
                </button>
              </div>
            ))}

            <div className="cart-page-footer-row">
              <Link to="/shop" className="btn-outline-navy cart-continue-btn">
                <i className="fas fa-arrow-left" /> Continue Shopping
              </Link>
              <p className="cart-secure-note">
                <i className="fas fa-shield-halved cart-secure-icon" />
                Secure checkout powered by SSL encryption
              </p>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="order-summary-box">
              <h3 className="order-summary-title">Order Summary</h3>

              {cart.map(i => (
                <div key={`${i.id}-${i.variant}`} className="order-summary-line">
                  <span className="order-summary-line-name">
                    {i.name} ×{i.qty}
                  </span>
                  <span className="order-summary-line-price">
                    ₹{(i.price * i.qty).toLocaleString('en-IN')}
                    {shipping > 0 && Number(i.shippingFee) > 0 && (
                      <small className="d-block">
                        + ₹{(Number(i.shippingFee) * i.qty).toLocaleString('en-IN')} shipping
                      </small>
                    )}
                  </span>
                </div>
              ))}

              <div className="order-summary-totals">
                <div className="order-row"><span>Subtotal</span><span>₹{cartTotal.toLocaleString('en-IN')}</span></div>
                <div className="order-row">
                  <span>Shipping</span>
                  <span className="order-shipping-free">
                    {shipping === 0 ? 'FREE' : `₹${shipping.toLocaleString('en-IN')}`}
                  </span>
                </div>
                {shipping > 0 && typeof freeShippingAbove === 'number' && (
                  <p className="order-shipping-note">
                    Add ₹{(freeShippingAbove - cartTotal).toLocaleString('en-IN')} more for free shipping
                  </p>
                )}
                <div className="order-row total">
                  <span>Total</span>
                  <span className="order-total-value">₹{(cartTotal + shipping).toLocaleString('en-IN')}</span>
                </div>
              </div>

              <Link to="/checkout" className="btn-gold cart-checkout-btn">
                <i className="fas fa-lock" /> Proceed to Checkout
              </Link>

              <div className="cart-payment-methods">
                {['fa-credit-card', 'fa-mobile-screen', 'fa-building-columns'].map(icon => (
                  <span key={icon} className="cart-payment-method">
                    <i className={`fas ${icon} cart-payment-icon`} />
                    {icon === 'fa-credit-card' ? 'Cards' : icon === 'fa-mobile-screen' ? 'UPI' : 'NetBanking'}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}